import { AssistantActionRef, AssistantEntityRef, AssistantKind, AssistantQuestion, AssistantTraceItem } from '../../types/ai.types';
import { AssistantProgress } from './assistantLive';

export interface AssistantResultCard {
  title?: string;
  facts?: string[];
  path?: string;
}

export interface AssistantChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  kind?: AssistantKind;
  action?: AssistantActionRef | null;
  confirmed?: boolean;
  progress?: AssistantProgress;
  result?: AssistantResultCard | null;
  entity?: AssistantEntityRef | null;
  trace?: AssistantTraceItem[];
  questions?: AssistantQuestion[];
  suggest?: { mode: 'action'; label: string; prompt: string } | null;
}

export interface AssistantThread {
  id: string;
  title: string;
  updatedAt: number;
  messages: AssistantChatMessage[];
}

const MAX_THREADS = 20;
const MAX_MESSAGES = 40;

export function threadKey(storeId: number): string {
  return `zeedcom-assistant-threads:${storeId}`;
}

export function createId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function asMessage(raw: unknown): AssistantChatMessage | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as AssistantChatMessage;
  if (item.role !== 'user' && item.role !== 'assistant') return null;
  const content = String(item.content || '').slice(0, 800);
  if (!content || !item.id) return null;
  return {
    id: String(item.id),
    role: item.role,
    content,
    kind: item.kind,
    action: item.action && item.action.id ? item.action : null,
    confirmed: Boolean(item.confirmed),
    progress: item.progress,
    result: item.result && typeof item.result === 'object' ? item.result : null,
    entity: item.entity && typeof item.entity === 'object' && item.entity.id
      ? { type: item.entity.type, id: Number(item.entity.id), name: item.entity.name }
      : null,
    trace: Array.isArray(item.trace)
      ? item.trace
        .filter((step) => step && step.id && step.label)
        .map((step) => ({
          id: String(step.id),
          label: String(step.label).slice(0, 80),
          details: Array.isArray(step.details)
            ? step.details.map((line) => String(line).slice(0, 60)).filter(Boolean).slice(0, 6)
            : undefined
        }))
        .slice(0, 6)
      : undefined,
    questions: Array.isArray(item.questions)
      ? item.questions
        .filter((ask) => ask && ask.id && ask.prompt && Array.isArray(ask.options))
        .map((ask) => ({
          id: String(ask.id),
          prompt: String(ask.prompt).slice(0, 120),
          options: ask.options
            .filter((option) => option && option.id && option.label)
            .map((option) => ({ id: String(option.id), label: String(option.label).slice(0, 80) }))
            .slice(0, 8)
        }))
        .slice(0, 3)
      : undefined,
    suggest: item.suggest && item.suggest.prompt
      ? {
        mode: 'action',
        label: String(item.suggest.label || 'Passer en Action').slice(0, 40),
        prompt: String(item.suggest.prompt).slice(0, 600)
      }
      : null
  };
}

export function loadThreads(storeId: number): { threads: AssistantThread[]; activeId: string | null } {
  try {
    const raw = localStorage.getItem(threadKey(storeId));
    if (!raw) return { threads: [], activeId: null };
    const parsed = JSON.parse(raw) as { threads?: unknown; activeId?: unknown };
    const threads = Array.isArray(parsed.threads)
      ? parsed.threads.slice(0, MAX_THREADS).map((entry) => {
        const thread = entry as AssistantThread;
        if (!thread || !thread.id) return null;
        const messages = Array.isArray(thread.messages)
          ? thread.messages.map(asMessage).filter((item): item is AssistantChatMessage => Boolean(item))
          : [];
        return {
          id: String(thread.id),
          title: String(thread.title || 'Conversation').slice(0, 80),
          updatedAt: Number(thread.updatedAt) || Date.now(),
          messages: messages.slice(-MAX_MESSAGES)
        };
      }).filter((item): item is AssistantThread => Boolean(item))
      : [];
    const activeId = typeof parsed.activeId === 'string' ? parsed.activeId : null;
    return { threads, activeId };
  } catch {
    return { threads: [], activeId: null };
  }
}

export function saveThreads(storeId: number, threads: AssistantThread[], activeId: string | null) {
  try {
    const payload = {
      activeId,
      threads: threads.slice(0, MAX_THREADS).map((thread) => ({
        ...thread,
        messages: thread.messages.slice(-MAX_MESSAGES)
      }))
    };
    localStorage.setItem(threadKey(storeId), JSON.stringify(payload));
  } catch {
    // quota local plein : la conversation reste en mémoire
  }
}

export function threadTitle(text: string, max = 42): string {
  const raw = String(text || '').replace(/\s+/g, ' ').trim();
  if (!raw) return 'Nouvelle';
  if (raw.length <= max) return raw;
  const slice = raw.slice(0, max);
  const cut = Math.max(slice.lastIndexOf(' '), slice.lastIndexOf('’'), slice.lastIndexOf('\''));
  const body = (cut > 18 ? slice.slice(0, cut) : slice).replace(/[.,;:!?]+$/, '').trim();
  return `${body}…`;
}

export function formatThreadTime(ts: number): string {
  const date = new Date(ts);
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  const diff = (start.getTime() - day.getTime()) / 86400000;
  const hour = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (diff < 1 && diff >= 0) return hour;
  if (diff < 2) return `Hier · ${hour}`;
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

export function formatThreadClock(ts: number): string {
  return new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export interface AssistantThreadGroup {
  id: string;
  label: string;
  threads: AssistantThread[];
}

export function groupThreads(threads: AssistantThread[], now = Date.now()): AssistantThreadGroup[] {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const groups: AssistantThreadGroup[] = [];
  const byId = new Map<string, AssistantThreadGroup>();
  const sorted = [...threads].sort((a, b) => b.updatedAt - a.updatedAt);
  for (const thread of sorted) {
    const day = new Date(thread.updatedAt);
    day.setHours(0, 0, 0, 0);
    const diff = Math.round((start.getTime() - day.getTime()) / 86400000);
    let id = `day-${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
    let label = day.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    if (diff <= 0) {
      id = 'today';
      label = 'Aujourd’hui';
    } else if (diff === 1) {
      id = 'yesterday';
      label = 'Hier';
    }
    let group = byId.get(id);
    if (!group) {
      group = { id, label, threads: [] };
      byId.set(id, group);
      groups.push(group);
    }
    group.threads.push(thread);
  }
  return groups;
}
